export interface SallaOAuthCallbackDraft {
  code: string | null
  state: string | null
  error: string | null
  errorDescription: string | null
  capturedAt: number
}

export type SallaOAuthCallbackParams = Omit<SallaOAuthCallbackDraft, 'capturedAt'>

const STORAGE_KEY = 'tlaaq_salla_oauth_callback'
const CALLBACK_MAX_AGE_MS = 30 * 60 * 1000

const EMPTY_CALLBACK: SallaOAuthCallbackParams = {
  code: null,
  state: null,
  error: null,
  errorDescription: null,
}

function canUseSessionStorage(): boolean {
  return typeof window !== 'undefined' && typeof window.sessionStorage !== 'undefined'
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function normalizeDraft(value: unknown): SallaOAuthCallbackDraft | null {
  if (!isRecord(value)) return null
  const capturedAt = typeof value.capturedAt === 'number' ? value.capturedAt : Date.now()
  if (Date.now() - capturedAt > CALLBACK_MAX_AGE_MS) return null
  const draft: SallaOAuthCallbackDraft = {
    code: typeof value.code === 'string' ? value.code : null,
    state: typeof value.state === 'string' ? value.state : null,
    error: typeof value.error === 'string' ? value.error : null,
    errorDescription: typeof value.errorDescription === 'string' ? value.errorDescription : null,
    capturedAt,
  }
  return hasCallbackParams(draft) ? draft : null
}

function paramsFromUrl(url: URL): SallaOAuthCallbackParams {
  return {
    code: url.searchParams.get('code'),
    state: url.searchParams.get('state'),
    error: url.searchParams.get('error'),
    errorDescription: url.searchParams.get('error_description'),
  }
}

function isSallaOAuthUrl(url: URL): boolean {
  const normalizedPath = url.pathname.replace(/\/+$/, '') || '/'
  return normalizedPath === '/salla-oauth'
}

export function hasCallbackParams(params: SallaOAuthCallbackParams): boolean {
  return Boolean(params.code || params.state || params.error || params.errorDescription)
}

export function saveSallaOAuthCallback(params: SallaOAuthCallbackParams): SallaOAuthCallbackDraft | null {
  if (!hasCallbackParams(params)) return null
  const draft: SallaOAuthCallbackDraft = { ...params, capturedAt: Date.now() }
  if (!canUseSessionStorage()) return draft
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(draft))
  } catch {
    return draft
  }
  return draft
}

export function captureSallaOAuthCallbackFromUrl(href: string = typeof window === 'undefined' ? '' : window.location.href): SallaOAuthCallbackDraft | null {
  if (!href) return null
  const url = new URL(href)
  if (!isSallaOAuthUrl(url)) return null
  return saveSallaOAuthCallback(paramsFromUrl(url))
}

export function readStoredSallaOAuthCallback(): SallaOAuthCallbackDraft | null {
  if (!canUseSessionStorage()) return null
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const draft = normalizeDraft(JSON.parse(raw))
    if (!draft) window.sessionStorage.removeItem(STORAGE_KEY)
    return draft
  } catch {
    return null
  }
}

export function hasStoredSallaOAuthCallback(): boolean {
  return readStoredSallaOAuthCallback() !== null
}

export function readSallaOAuthCallbackParams(): SallaOAuthCallbackParams {
  if (typeof window !== 'undefined') {
    const url = new URL(window.location.href)
    const fromUrl = paramsFromUrl(url)
    if (isSallaOAuthUrl(url) && hasCallbackParams(fromUrl)) {
      saveSallaOAuthCallback(fromUrl)
      return fromUrl
    }
  }

  const stored = readStoredSallaOAuthCallback()
  if (stored) {
    return {
      code: stored.code,
      state: stored.state,
      error: stored.error,
      errorDescription: stored.errorDescription,
    }
  }

  return EMPTY_CALLBACK
}

export function clearSallaOAuthCallback(): void {
  if (!canUseSessionStorage()) return
  try {
    window.sessionStorage.removeItem(STORAGE_KEY)
  } catch {
    // Ignore storage failures; the callback URL is stripped after success too.
  }
}
